'use client';

import { Box, Paper, useTheme } from "@mui/material";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import React from "react";
import FlyoutView from "@/app/features/sites/header/ui/desktop/flyout-info/FlyoutView";
import { SubmenuItem } from "@/app/features/sites/header/types/menu-items";

const DEFAULT_FLYOUT_HEIGHT = 420;
const CONTAINER_Y_DURATION = 0.18;
const CONTAINER_HEIGHT_DURATION = 0.32;
const CONTENT_SWITCH_DURATION = 0.1;

export default function FlyoutInfo({
    visibleFlyoutInfo,
    onEnter,
    onLeave,
    onFocus,
    onBlur,
    onKeyDown
}: {
    visibleFlyoutInfo: SubmenuItem['id'] | null ,
    onEnter?: () => void,
    onLeave?: () => void,
    onFocus?: (e: React.FocusEvent) => void,
    onBlur?: (e: React.FocusEvent) => void,
    onKeyDown?: (e: React.KeyboardEvent) => void
}) {
    const theme = useTheme();
    const reduceMotion = useReducedMotion();
    const contentMeasureRef = React.useRef<HTMLDivElement | null>(null);
    const measureRafRef = React.useRef<number | null>(null);
    const measuredHeightRef = React.useRef(0);
    const [contentHeight, setContentHeight] = React.useState(0);
    const [stableHeight, setStableHeight] = React.useState(0);

    React.useLayoutEffect(() => {
        if (!visibleFlyoutInfo) return;

        const element = contentMeasureRef.current;
        if (!element) return;

        const commitHeight = (nextHeight: number) => {
            if (nextHeight <= 0 || nextHeight === measuredHeightRef.current) return;

            measuredHeightRef.current = nextHeight;
            setContentHeight(nextHeight);
            setStableHeight(nextHeight);
        };

        const measureHeight = () => {
            commitHeight(Math.round(element.offsetHeight));
        };

        const scheduleHeightMeasure = () => {
            if (measureRafRef.current !== null) {
                cancelAnimationFrame(measureRafRef.current);
            }
            measureRafRef.current = requestAnimationFrame(() => {
                measureRafRef.current = null;
                measureHeight();
            });
        };

        scheduleHeightMeasure();

        const observer = new ResizeObserver(scheduleHeightMeasure);
        observer.observe(element);

        return () => {
            observer.disconnect();
            if (measureRafRef.current !== null) {
                cancelAnimationFrame(measureRafRef.current);
                measureRafRef.current = null;
            }
        };
    }, [visibleFlyoutInfo]);

    React.useEffect(() => {
        if (!visibleFlyoutInfo) {
            setContentHeight(0);
        }
    }, [visibleFlyoutInfo]);

    const animatedHeight = visibleFlyoutInfo
        ? (contentHeight || stableHeight || DEFAULT_FLYOUT_HEIGHT)
        : 0;

    return (
        <AnimatePresence mode="wait" initial={false}>
            {visibleFlyoutInfo && (
                <MotionPaper
                    key="flyout-container"
                    elevation={8}
                    className="flyout-container fixed left-0 right-0 overflow-hidden"
                    initial={reduceMotion ? { y: 0, height: animatedHeight } : { y: '-100%', height: 0 }}
                    animate={{ y: 0, height: animatedHeight }}
                    exit={reduceMotion ? { y: 0, height: animatedHeight } : { y: '-100%', height: 0 }}
                    onMouseEnter={onEnter}
                    onMouseLeave={onLeave}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    transition={reduceMotion
                        ? { duration: 0 }
                        : {
                            y: { duration: CONTAINER_Y_DURATION, ease: [0.2, 0, 0, 1] },
                            height: { duration: CONTAINER_HEIGHT_DURATION, ease: [0.22, 1, 0.36, 1] },
                        }
                    }
                    sx={{
                        top: 'var(--header-height, 132px)',
                        zIndex: theme.zIndex.appBar - 1,
                        boxShadow: (theme) => theme.shadows[8],
                        borderBottom: (theme) =>  `1px solid ${theme.palette.primary.dark}`,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomLeftRadius: 16,
                        borderBottomRightRadius: 16,
                        willChange: 'transform',
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden',
                        transformOrigin: 'top center',
                    }}
                >
                    <Box
                        ref={contentMeasureRef}
                        sx={{
                            position: 'relative',
                            minHeight: 0,
                        }}
                    >
                        <AnimatePresence initial={false} mode="wait">
                            <MotionBox
                                key={visibleFlyoutInfo}
                                initial={reduceMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: 6 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={reduceMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -6 }}
                                transition={reduceMotion
                                    ? { duration: 0 }
                                    : { duration: CONTENT_SWITCH_DURATION, ease: [0.2, 0, 0, 1] }
                                }
                                sx={{
                                    width: '100%',
                                    minHeight: 0,
                                }}
                            >
                                <FlyoutView activeMenuId={visibleFlyoutInfo} />
                            </MotionBox>
                        </AnimatePresence>
                    </Box>
                </MotionPaper>
            )}
        </AnimatePresence>
    );
}

const MotionPaper = motion.create(Paper);
const MotionBox = motion.create(Box);
